import { useState, useEffect, useMemo, useCallback, useId } from "react";
import { useNavigate } from "react-router-dom";
import { encryptIdForUrl } from "@/utils/urlCrypto";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import { KPICard } from "@/components/dashboard/KPICard";
import { ChartCard } from "@/components/dashboard/ChartCard";
import { SolicitacaoStatusBadge } from "@/components/solicitacoes/SolicitacaoStatusBadge";
import { type StatusSolicitacao } from "@/types/solicitacao";
import { useAgenteAtualStore } from "@/stores/agenteAtualStore";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  FileText,
  Clock,
  CheckCircle2,
  AlertTriangle,
  MapPin,
  Loader2,
  XCircle,
  Calendar,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { RemoteDataSource } from "@/datasources/remote_datasource";
import { AppConfig } from "@/datasources/AppConfig";
import { SolicitacaoParcial, SolicitacaoParcialModel } from "@/components/solicitacoes/core/models";

const api = new RemoteDataSource("https://api.toolbox.app.br/urbnet");

interface Endereco {
  bairro?: string;
  cidade?: string;
  logradouro?: string;
  estado?: string;
}

interface Solicitacao {
  id: string;
  protocolo: string;
  status: StatusSolicitacao;
  estadoAprovacao: string;
  arquivada: boolean;
  dataCriacao: string;
  dataConclusao?: string;
  totalApoiadores: number;
  topico: { id: string; descricao: string; etiquetas?: string[] };
  endereco?: Endereco;
  agenteResponsavel?: AgenteCompacto;
}

interface AgenteCompacto {
  id: string;
  nome: string;
  ouvidoria?: { id: string; nome: string };
  gestor?: boolean;
  status?: string;
  arquivado?: boolean;
  fotoPerfil?: { id: string; arquivo: string };
}

interface SolicitacaoFull extends Solicitacao {
  agenteResponsavel?: AgenteCompacto;
  ouvidoriaResponsavel?: { id: string; nome: string };
  latitude?: number | null;
  longitude?: number | null;
  altitude?: number | null;
  anexos?: { id: string; arquivo: string }[];
  apoiando?: boolean;
  criadaPorMim?: boolean;
  upn?: string;
}

// Normaliza: remove acentos, espaços, underscores e deixa lowercase.
// Necessário porque a API retorna valores como "Em análise" e "Aguardando Aprovação".
const normalize = (v: string) =>
  (v || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\s_-]+/g, "")
    .toLowerCase();

const statusMatch = (raw: string) => (s: Solicitacao) =>
  normalize(s.status) === normalize(raw);

const aprovacaoMatch = (raw: string) => (s: Solicitacao) =>
  normalize(s.estadoAprovacao) === normalize(raw);

// Chave de data local (YYYY-MM-DD) — evita problemas de timezone com toISOString()
function toLocalDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Agrupa solicitações por dia — de hoje retroativamente pelo período
function buildTimelineFor(
  solicitacoes: Solicitacao[],
  days: number,
  predicate: (s: Solicitacao) => boolean,
  dateSource?: (s: Solicitacao) => string | undefined,
): { name: string; value: number }[] {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const getDate = dateSource ?? ((s) => s.dataCriacao);
  const points: { name: string; value: number }[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const dayStr = toLocalDateKey(d);
    const label = d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const value = solicitacoes.filter((s) => {
      const raw = getDate(s);
      if (!raw) return false;
      return toLocalDateKey(new Date(raw)) === dayStr && predicate(s);
    }).length;
    points.push({ name: label, value });
  }
  return points;
}

// Agrupa solicitações por dia — para intervalo personalizado
function buildTimelineForRange(
  solicitacoes: Solicitacao[],
  fromDate: Date,
  toDate: Date,
  predicate: (s: Solicitacao) => boolean,
  dateSource?: (s: Solicitacao) => string | undefined,
): { name: string; value: number }[] {
  const points: { name: string; value: number }[] = [];
  const start = new Date(fromDate);
  start.setHours(12, 0, 0, 0);
  const end = new Date(toDate);
  end.setHours(12, 0, 0, 0);
  const diffTime = end.getTime() - start.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  const getDate = dateSource ?? ((s) => s.dataCriacao);
  for (let i = 0; i < diffDays; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    const dayStr = toLocalDateKey(d);
    const label = d.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const value = solicitacoes.filter((s) => {
      const raw = getDate(s);
      if (!raw) return false;
      return toLocalDateKey(new Date(raw)) === dayStr && predicate(s);
    }).length;
    points.push({ name: label, value });
  }
  return points;
}

// Agrupa solicitações por mês/ano — usado no filtro "Todo o tempo"
function buildTimelineAllTimeByMonth(
  solicitacoes: Solicitacao[],
  predicate: (s: Solicitacao) => boolean,
  dateSource?: (s: Solicitacao) => string | undefined,
): { name: string; value: number }[] {
  const hoje = new Date();
  hoje.setHours(12, 0, 0, 0);
  const getDate = dateSource ?? ((s) => s.dataCriacao);

  const datas = solicitacoes
    .map((s) => new Date(s.dataCriacao))
    .filter((d) => !isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  if (datas.length === 0) return [];

  const primeiro = datas[0];
  const map: Record<string, number> = {};

  for (const s of solicitacoes) {
    const raw = getDate(s);
    if (!raw || !predicate(s)) continue;
    const d = new Date(raw);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    map[key] = (map[key] || 0) + 1;
  }

  const points: { name: string; value: number }[] = [];
  const cursor = new Date(primeiro.getFullYear(), primeiro.getMonth(), 1);
  const fim = new Date(hoje.getFullYear(), hoje.getMonth(), 1);

  while (cursor <= fim) {
    const ano = cursor.getFullYear();
    const mes = cursor.getMonth();
    const key = `${ano}-${String(mes + 1).padStart(2, "0")}`;
    const label = `${String(mes + 1).padStart(2, "0")}/${String(ano).slice(-2)}`;
    points.push({ name: label, value: map[key] || 0 });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return points;
}

// Normaliza o status da API para o tipo aceito pelo StatusBadge
function mapStatus(
  status: string,
): "open" | "in_progress" | "resolved" | "critical" | "pending" {
  const s = status?.toLowerCase();
  if (s === "aberta" || s === "open") return "open";
  if (s === "emandamento" || s === "em_andamento" || s === "in_progress")
    return "in_progress";
  if (s === "resolvida" || s === "resolved") return "resolved";
  if (s === "critica" || s === "critical") return "critical";
  return "pending";
}

// Função para formatar data no estilo da SolicitacoesPage
const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return dateString;
  }
};

const GradientFavoriteIcon = ({
  className = "h-5 w-5",
}: {
  className?: string;
}) => {
  const gradientId = useId();
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(var(--primary))" />
          <stop offset="100%" stopColor="hsl(var(--secondary))" />
        </linearGradient>
      </defs>
      <path fill="none" d="M0 0h24v24H0V0z" />
      <path
        fill={`url(#${gradientId})`}
        stroke={`url(#${gradientId})`}
        strokeWidth="1.25"
        strokeLinejoin="round"
        d="M20.2246094,4.5693359C17.9908447,2.3260498,14.4058838,2.1826172,12,4.2402344C9.5941162,2.1826172,6.0091553,2.3260498,3.7753906,4.5693359c-2.3632812,2.3798218-2.3632812,6.2207642,0,8.6005859l7.8701172,7.8955078c0.09375,0.0944824,0.2213745,0.147583,0.3544922,0.1474609c0.1331177,0.0001221,0.2607422-0.0529785,0.3544922-0.1474609l7.8701172-7.8955078C22.5878906,10.7901001,22.5878906,6.9491577,20.2246094,4.5693359z"
      />
    </svg>
  );
};

export default function DashboardPage() {
  const navigate = useNavigate();

  const [solicitacoes, setSolicitacoes] = useState<Solicitacao[]>([]);
  const [solicitacoesFull, setSolicitacoesFull] = useState<SolicitacaoFull[]>([]);
  const [solicitacoesRecentes, setSolicitacoesRecentes] = useState<SolicitacaoParcial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [periodo, setPeriodo] = useState("7d");
  const [selectedSeries, setSelectedSeries] = useState<Set<string>>(new Set(["pendente", "emAnalise", "agendada", "resolvida", "aguardando", "aprovada", "reprovada"]));
  const [showDateModal, setShowDateModal] = useState(false);
  const [showYearModal, setShowYearModal] = useState(false);
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [appliedDateRange, setAppliedDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [ano, setAno] = useState<number | undefined>(undefined);
  const [appliedAno, setAppliedAno] = useState<number | undefined>(undefined);
  const [fromMonth, setFromMonth] = useState<Date>(new Date());
  const [toMonth, setToMonth] = useState<Date>(new Date());


  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Verificar se municipioId existe, se não, buscar agente atual novamente
      if (!AppConfig.municipioId) {
        const fetchAgenteAtual = useAgenteAtualStore.getState().fetchAgenteAtual;
        await fetchAgenteAtual();
      }

      // Se ainda não tiver municipioId, não fazer requisições
      if (!AppConfig.municipioId) {
        setIsLoading(false);
        return;
      }

      // Preparar parâmetros de data
      const queryParams: Record<string, string | number> = {};

      if (periodo === "Anual") {
        if (!appliedAno) {
          // Aguardando confirmação do ano no dialog: limpa dados sem fazer requisição
          setSolicitacoes([]);
          setIsLoading(false);
          return;
        }
        // Usar DataInicio e DataFim para o ano selecionado
        const start = new Date(appliedAno, 0, 1, 0, 0, 0, 0);
        const end = new Date(appliedAno, 11, 31, 23, 59, 59, 999);
        queryParams.DataInicio = start.toISOString();
        queryParams.DataFim = end.toISOString();
      } else if (periodo === "AllTime") {
        // Todo o tempo: a API aplica filtro padrão quando nenhum parâmetro de data é enviado,
        // então enviamos um intervalo amplo para garantir todo o histórico disponível.
        const start = new Date(1900, 0, 1, 0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        queryParams.DataInicio = start.toISOString();
        queryParams.DataFim = end.toISOString();
      } else if (periodo === "custom" && appliedDateRange.from && appliedDateRange.to) {
        // Usar DataInicio e DataFim para intervalo personalizado
        queryParams.DataInicio = appliedDateRange.from.toISOString();
        queryParams.DataFim = appliedDateRange.to.toISOString();
      } else if (periodo !== "24h" && periodo !== "custom") {
        // Usar DataInicio e DataFim para 7, 30, 90 dias garantindo o período exato
        const days = periodo === "7d" ? 7 : periodo === "30d" ? 30 : 90;
        const start = new Date();
        start.setHours(0, 0, 0, 0);
        start.setDate(start.getDate() - days - 1);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        queryParams.DataInicio = start.toISOString();
        queryParams.DataFim = end.toISOString();
      }

      // Buscar solicitações com formato compact (retorna array com topico.id)
      const solResult = await api.get<Solicitacao[]>({
        endpoint: `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}/solicitacoes`,
        headers: { Accept: "application/vnd.data.grafics.v1+json" },
        queryParams,
        forceRefresh: true,
        serialize: (raw: unknown) => {
          const data = raw as Record<string, unknown>;
          if (Array.isArray(raw)) return raw as Solicitacao[];
          if (data?.items && Array.isArray(data.items)) return data.items as Solicitacao[];
          if (data?.data && Array.isArray(data.data)) return data.data as Solicitacao[];
          return [];
        },
      });

      let solicitacoesList: Solicitacao[] = [];
      if (!("failure" in solResult)) {
        const resultData = solResult.success.data;
        solicitacoesList = Array.isArray(resultData) ? resultData : [];
      }
      setSolicitacoes(solicitacoesList);
    } catch (err) {
      // Error fetching dashboard data — garante que as contagens fiquem zeradas
      setSolicitacoes([]);
    } finally {
      setIsLoading(false);
    }
  }, [periodo, appliedAno, appliedDateRange]);

  // Busca dados estatísticos completos (inclui agenteResponsavel) usando minimal.v1
  const fetchDataStats = useCallback(async () => {
    try {
      if (!AppConfig.municipioId) {
        const fetchAgenteAtual = useAgenteAtualStore.getState().fetchAgenteAtual;
        await fetchAgenteAtual();
      }
      if (!AppConfig.municipioId) return;

      const queryParams: Record<string, string | number> = {};
      if (periodo === "Anual") {
        if (!appliedAno) return;
        const start = new Date(appliedAno, 0, 1, 0, 0, 0, 0);
        const end = new Date(appliedAno, 11, 31, 23, 59, 59, 999);
        queryParams.DataInicio = start.toISOString();
        queryParams.DataFim = end.toISOString();
      } else if (periodo === "AllTime") {
        const start = new Date(1900, 0, 1, 0, 0, 0, 0);
        const end = new Date();
        end.setHours(23, 59, 59, 999);
        queryParams.DataInicio = start.toISOString();
        queryParams.DataFim = end.toISOString();
      } else if (periodo === "custom" && appliedDateRange.from && appliedDateRange.to) {
        queryParams.DataInicio = appliedDateRange.from.toISOString();
        queryParams.DataFim = appliedDateRange.to.toISOString();
      } else if (periodo !== "24h" && periodo !== "custom") {
        const days = periodo === "7d" ? 7 : periodo === "30d" ? 30 : 90;
        queryParams.UltimosDias = days;
      }

      const result = await api.get<SolicitacaoFull[]>({
        endpoint: `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}/solicitacoes`,
        headers: { Accept: "application/vnd.data.minimal.v1+json" },
        queryParams,
        forceRefresh: true,
        serialize: (raw: unknown) => {
          const data = raw as Record<string, unknown>;
          if (Array.isArray(raw)) return raw as SolicitacaoFull[];
          if (data?.items && Array.isArray(data.items)) return data.items as SolicitacaoFull[];
          if (data?.data && Array.isArray(data.data)) return data.data as SolicitacaoFull[];
          return [];
        },
      });

      const allItems = !("failure" in result)
        ? Array.isArray(result.success.data) ? result.success.data : []
        : [];
      setSolicitacoesFull(allItems);
      // DEBUG: inspecionar estrutura dos dados
      if (allItems.length > 0) {
        console.log("[DEBUG] Primeira solicitacao:", allItems[0]);
        console.log("[DEBUG] agenteResponsavel:", (allItems[0] as unknown as Record<string, unknown>).agenteResponsavel);
      }
    } catch {
      setSolicitacoesFull([]);
    }
  }, [periodo, appliedAno, appliedDateRange]);

  const fetchRecentes = useCallback(async () => {
    try {
      if (!AppConfig.municipioId) {
        const fetchAgenteAtual = useAgenteAtualStore.getState().fetchAgenteAtual;
        await fetchAgenteAtual();
      }
      if (!AppConfig.municipioId) return;

      const result = await api.get<unknown[]>({
        endpoint: `v1/municipios/${AppConfig.municipioId}/ouvidorias/${AppConfig.ouvidoriaId}/solicitacoes`,
        headers: { Accept: "application/vnd.data.partial.v1+json" },
        queryParams: {
          PageNumber: 1,
          PageSize: 3,
        },
        forceRefresh: true,
        serialize: (raw: unknown) => {
          if (Array.isArray(raw)) return raw as unknown[];
          const data = raw as Record<string, unknown>;
          if (data?.items && Array.isArray(data.items)) return data.items as unknown[];
          if (data?.data && Array.isArray(data.data)) return data.data as unknown[];
          return [];
        },
      });

      if (!("failure" in result)) {
        const items = Array.isArray(result.success.data) ? result.success.data : [];
        const mapped = items.map((item) => SolicitacaoParcialModel.fromMap(item));
        setSolicitacoesRecentes(mapped);
      } else {
        setSolicitacoesRecentes([]);
      }
    } catch {
      setSolicitacoesRecentes([]);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchDataStats();
    fetchRecentes();
  }, [fetchData, fetchDataStats, fetchRecentes]);

  // Sempre que a página ganhar foco ou voltar à aba, recarrega os dados
  useEffect(() => {
    const handleFocus = () => {
      fetchData();
      fetchDataStats();
      fetchRecentes();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchData();
        fetchDataStats();
        fetchRecentes();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchData, fetchDataStats, fetchRecentes]);

  const days =
    periodo === "24h" ? 1 : periodo === "7d" ? 7 : periodo === "30d" ? 30 : 90;

  // Configuração de cada card de status (baseado em STATUS_COLORS de SolicitacoesPage)
  const allCharts = useMemo(
    () => [
      // Status só entram no gráfico correspondente se estiverem Aprovadas.
      // As que estão Aguardando/Reprovada aparecem somente no gráfico de aprovação.
      {
        key: "pendente",
        label: "Pendente",
        color: "hsl(var(--destructive))",
        predicate: (s: Solicitacao) =>
          statusMatch("Pendente")(s) && aprovacaoMatch("Aprovada")(s),
        category: "Status",
      },
      {
        key: "emAnalise",
        label: "Em Análise",
        color: "hsl(25, 95%, 53%)",
        predicate: (s: Solicitacao) =>
          statusMatch("Em análise")(s) && aprovacaoMatch("Aprovada")(s),
        category: "Status",
      },
      {
        key: "agendada",
        label: "Agendada",
        color: "#CEDC38",
        predicate: (s: Solicitacao) =>
          statusMatch("Agendada")(s) && aprovacaoMatch("Aprovada")(s),
        category: "Status",
      },
      {
        key: "resolvida",
        label: "Resolvida",
        color: "hsl(var(--success))",
        predicate: (s: Solicitacao) =>
          statusMatch("Resolvida")(s) && aprovacaoMatch("Aprovada")(s),
        dateSource: (s: Solicitacao) => s.dataConclusao || s.dataCriacao,
        category: "Status",
      },
      {
        key: "aguardando",
        label: "Aguardando Aprovação",
        color: "hsl(var(--destructive))",
        predicate: aprovacaoMatch("Aguardando Aprovação"),
        category: "Aprovação",
      },
      {
        key: "aprovada",
        label: "Aprovada",
        color: "hsl(var(--primary))",
        predicate: aprovacaoMatch("Aprovada"),
        category: "Aprovação",
      },
      {
        key: "reprovada",
        label: "Reprovada",
        color: "hsl(var(--muted-foreground))",
        predicate: aprovacaoMatch("Reprovada"),
        category: "Aprovação",
      },
    ],
    [],
  );

  const allSeries = useMemo(
    () =>
      allCharts.map((cfg) => {
        let data: { name: string; value: number }[];
        if (periodo === "custom" && appliedDateRange.from && appliedDateRange.to) {
          data = buildTimelineForRange(solicitacoes, appliedDateRange.from, appliedDateRange.to, cfg.predicate, cfg.dateSource);
        } else if (periodo === "Anual" && appliedAno) {
          data = buildTimelineForRange(
            solicitacoes,
            new Date(appliedAno, 0, 1),
            new Date(appliedAno, 11, 31),
            cfg.predicate,
            cfg.dateSource,
          );
        } else if (periodo === "AllTime") {
          data = buildTimelineAllTimeByMonth(solicitacoes, cfg.predicate, cfg.dateSource);
        } else {
          data = buildTimelineFor(solicitacoes, days, cfg.predicate, cfg.dateSource);
        }
        return { ...cfg, data };
      }),
    [solicitacoes, days, allCharts, periodo, appliedDateRange, appliedAno],
  );

  const visibleSeries = useMemo(
    () => allSeries.filter((s) => selectedSeries.has(s.key)),
    [allSeries, selectedSeries],
  );

  // Mescla dados de todas as séries visíveis em um único array para o gráfico combinado
  const combinedChartData = useMemo(() => {
    if (visibleSeries.length === 0) return [];
    
    // Pega os dados da primeira série como base (tem as datas)
    const baseData = visibleSeries[0].data;
    
    // Para cada ponto de data, adiciona valores de todas as séries
    return baseData.map((point, index) => {
      const mergedPoint: Record<string, string | number> = { name: point.name };
      
      // Adiciona o valor de cada série visível usando sua key como dataKey
      visibleSeries.forEach((series) => {
        mergedPoint[series.key] = series.data[index]?.value ?? 0;
      });
      
      return mergedPoint;
    });
  }, [visibleSeries]);

  const toggleSeries = (key: string) => {
    setSelectedSeries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Filtra pelo período selecionado
  const filtered = useMemo(() => {
    if (periodo === "custom" && appliedDateRange.from && appliedDateRange.to) {
      const from = new Date(appliedDateRange.from);
      from.setHours(0, 0, 0, 0);
      const to = new Date(appliedDateRange.to);
      to.setHours(23, 59, 59, 999);
      return solicitacoes.filter((s) => {
        const creationDate = new Date(s.dataCriacao);
        return creationDate >= from && creationDate <= to;
      });
    }
    if (periodo === "Anual") {
      if (!appliedAno) return [];
      const start = new Date(appliedAno, 0, 1, 0, 0, 0, 0);
      const end = new Date(appliedAno, 11, 31, 23, 59, 59, 999);
      return solicitacoes.filter((s) => {
        const creationDate = new Date(s.dataCriacao);
        return creationDate >= start && creationDate <= end;
      });
    }
    if (periodo === "AllTime") {
      return solicitacoes;
    }
    const cutoff = new Date();
    cutoff.setHours(0, 0, 0, 0);
    cutoff.setDate(cutoff.getDate() - days + 1);
    return solicitacoes.filter((s) => new Date(s.dataCriacao) >= cutoff);
  }, [solicitacoes, days, periodo, appliedDateRange, appliedAno]);

  // KPIs — Status só contam quando estadoAprovacao = Aprovada.
  // Aguardando Aprovação e Reprovada têm contadores isolados,
  // para garantir que cada solicitação apareça em UM único card.
  const isAprovada = (s: Solicitacao) =>
    normalize(s.estadoAprovacao) === normalize("Aprovada");

  const pendentes = filtered.filter(
    (s) => normalize(s.status) === normalize("Pendente") && isAprovada(s),
  ).length;
  const agendadas = filtered.filter(
    (s) => normalize(s.status) === normalize("Agendada") && isAprovada(s),
  ).length;
  const emAnalise = filtered.filter(
    (s) => normalize(s.status) === normalize("Em análise") && isAprovada(s),
  ).length;
  const resolvidas = solicitacoes.filter((s) => {
    if (normalize(s.status) !== normalize("Resolvida") || !isAprovada(s)) return false;
    const raw = s.dataConclusao || s.dataCriacao;
    if (!raw) return false;
    const d = new Date(raw);

    if (periodo === "custom" && appliedDateRange.from && appliedDateRange.to) {
      const from = new Date(appliedDateRange.from);
      from.setHours(0, 0, 0, 0);
      const to = new Date(appliedDateRange.to);
      to.setHours(23, 59, 59, 999);
      return d >= from && d <= to;
    }

    if (periodo === "Anual") {
      if (!appliedAno) return false;
      const start = new Date(appliedAno, 0, 1, 0, 0, 0, 0);
      const end = new Date(appliedAno, 11, 31, 23, 59, 59, 999);
      return d >= start && d <= end;
    }

    if (periodo === "AllTime") return true;

    const cutoff = new Date();
    cutoff.setHours(0, 0, 0, 0);
    cutoff.setDate(cutoff.getDate() - days + 1);
    return d >= cutoff;
  }).length;
  const reprovadas = filtered.filter(
    (s) => normalize(s.estadoAprovacao) === normalize("Reprovada"),
  ).length;
  const aguardandoAprovacao = filtered.filter(
    (s) => normalize(s.estadoAprovacao) === normalize("Aguardando Aprovação"),
  ).length;
  const total = filtered.length;

  // Total de apoios acumulados nas solicitações do período
  const totalApoios = useMemo(
    () => filtered.reduce((acc, s) => acc + (s.totalApoiadores || 0), 0),
    [filtered],
  );

  interface SolicitacaoComDias extends SolicitacaoFull {
    diasAberto: number;
  }

  // Pontos de atenção: solicitações em aberto há mais de 30 dias
  const pontosAtencao = useMemo<SolicitacaoComDias[]>(() => {
    const hoje = new Date();
    return solicitacoesFull
      .filter((s) => {
        const st = normalize(s.status);
        return st !== normalize("Resolvida") && st !== normalize("Reprovada");
      })
      .map((s) => ({
        ...s,
        diasAberto: Math.floor((hoje.getTime() - new Date(s.dataCriacao).getTime()) / (1000 * 60 * 60 * 24)),
      }))
      .filter((s) => s.diasAberto > 30)
      .sort((a, b) => b.diasAberto - a.diasAberto)
      .slice(0, 10) as SolicitacaoComDias[];
  }, [solicitacoesFull]);

  // Recentes vêm de requisição separada com partial v2
  const recentes = solicitacoesRecentes;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Carregando dados...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Pendentes"
          value={String(pendentes)}
          icon={FileText}
          color="#ef4444" // mesmo do allCharts[0].color
        />
        <KPICard
          title="Agendadas"
          value={String(agendadas)}
          icon={Clock}
          color="#CEDC38" // mesmo do allCharts[2].color
        />
        <KPICard
          title="Em Análise"
          value={String(emAnalise)}
          icon={AlertTriangle}
          color="#f97316" // mesmo do allCharts[1].color
        />
        <KPICard
          title="Resolvidas"
          value={String(resolvidas)}
          icon={CheckCircle2}
          color="#22c55e" // mesmo do allCharts[3].color
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <KPICard
          title="Apoios"
          value={String(totalApoios)}
          icon={GradientFavoriteIcon}
          color="hsl(var(--info))"
          subtitle="Total de apoios nas solicitações"
        />
        <KPICard
          title="Reprovadas"
          value={String(reprovadas)}
          icon={XCircle}
          color="hsl(var(--muted-foreground))" // mesmo do allCharts[6].color
        />
        <KPICard
          title="Aguardando Aprovação"
          value={String(aguardandoAprovacao)}
          icon={Clock}
          color="#ef4444" // mesmo do allCharts[4].color
        />
        <KPICard
          title="Total Geral"
          value={String(solicitacoes.length)}
          icon={FileText}
          variant="primary"
        />
      </div>

      {/* Evolução Combinada */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="heading-subsection text-foreground">Evolução Temporal</h3>
            <p className="text-caption mt-0.5">Série temporal de status e estados de aprovação no período</p>
          </div>
          <Select value={periodo} onValueChange={(value) => {
            if (value === "custom") {
              setShowDateModal(true);
            } else if (value === "Anual") {
              setPeriodo("Anual");
              setAppliedAno(undefined);
              setAno(appliedAno ?? new Date().getFullYear());
              setShowYearModal(true);
            } else {
              setPeriodo(value);
              setAppliedAno(undefined);
              setAno(undefined);
            }
          }}>
            <SelectTrigger className="w-auto min-w-[140px] max-w-[300px]">
              <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
              <SelectValue placeholder="Período">
                {periodo === "custom" && appliedDateRange.from && appliedDateRange.to ? (
                  <span className="text-sm truncate">
                    de {appliedDateRange.from.toLocaleDateString("pt-BR")} até {appliedDateRange.to.toLocaleDateString("pt-BR")}
                  </span>
                ) : periodo === "Anual" && appliedAno ? (
                  <span className="text-sm truncate">Ano: {appliedAno}</span>
                ) : (
                  periodo === "7d" ? "7 dias" :
                  periodo === "30d" ? "30 dias" :
                  periodo === "90d" ? "90 dias" :
                  periodo === "AllTime" ? "Todo o tempo" :
                  periodo === "Anual" ? "Anual" : "Personalizado"
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">7 dias</SelectItem>
              <SelectItem value="30d">30 dias</SelectItem>
              <SelectItem value="90d">90 dias</SelectItem>
              <SelectItem value="AllTime">Todo o tempo</SelectItem>
              <SelectItem value="Anual">Anual</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="grid gap-4 lg:grid-cols-5">
          {/* Controles Laterais */}
          <div className="surface-elevated p-5 space-y-5">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Status</h4>
              <div className="space-y-2">
                {allCharts.filter(c => c.category === "Status").map((chart) => (
                  <div key={chart.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={chart.key}
                      checked={selectedSeries.has(chart.key)}
                      onCheckedChange={() => toggleSeries(chart.key)}
                      className="data-[state=checked]:bg-[var(--check-color)]"
                      style={{ '--check-color': chart.color } as React.CSSProperties}
                    />
                    <Label
                      htmlFor={chart.key}
                      className="flex items-center gap-2 cursor-pointer flex-1 text-sm"
                    >
                      <span
                        className="h-2 w-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: chart.color }}
                      />
                      <span>{chart.label}</span>
                    </Label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Aprovação</h4>
              <div className="space-y-2">
                {allCharts.filter(c => c.category === "Aprovação").map((chart) => (
                  <div key={chart.key} className="flex items-center space-x-2">
                    <Checkbox
                      id={chart.key}
                      checked={selectedSeries.has(chart.key)}
                      onCheckedChange={() => toggleSeries(chart.key)}
                      className="data-[state=checked]:bg-[var(--check-color)]"
                      style={{ '--check-color': chart.color } as React.CSSProperties}
                    />
                    <Label
                      htmlFor={chart.key}
                      className="flex items-center gap-2 cursor-pointer flex-1 text-sm"
                    >
                      <span
                        className="h-2 w-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: chart.color }}
                      />
                      <span>{chart.label}</span>
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Gráfico Grande Combinado */}
          <div className="lg:col-span-4 surface-elevated p-6">
            <div className="h-[500px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={combinedChartData} margin={{ top: 20, right: 20, left: 10, bottom: 20 }}>
                  <defs>
                    {visibleSeries.map((series) => (
                      <linearGradient
                        key={`grad-${series.key}`}
                        id={`grad-${series.key}`}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop offset="5%" stopColor={series.color} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={series.color} stopOpacity={0.01} />
                      </linearGradient>
                    ))}
                  </defs>
                  <CartesianGrid
                    strokeDasharray="2 4"
                    stroke="hsl(var(--muted-foreground)/0.15)"
                    vertical={true}
                    horizontal={true}
                  />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))", fontWeight: 500 }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
                    dy={8}
                    interval={
                      periodo === "7d" ? 0 :
                      periodo === "30d" ? 2 :
                      periodo === "90d" ? 6 :
                      "preserveStartEnd"
                    }
                    angle={periodo === "30d" || periodo === "90d" ? -45 : 0}
                    height={periodo === "30d" || periodo === "90d" ? 50 : 30}
                    tickMargin={periodo === "30d" || periodo === "90d" ? 8 : 4}
                    tickFormatter={(value) => {
                      // Formata data para mostrar só dia/mês se for longo
                      const parts = value.split('/');
                      if (parts.length === 3) {
                        return `${parts[0]}/${parts[1]}`;
                      }
                      return value;
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))", fontWeight: 500 }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))", strokeWidth: 1 }}
                    allowDecimals={false}
                    dx={-8}
                    tickCount={6}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      return (
                        <div
                          style={{
                            backgroundColor: "hsl(var(--popover))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: "12px",
                            fontSize: "13px",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                            color: "hsl(var(--foreground))",
                            padding: "12px 16px",
                            minWidth: "220px",
                          }}
                        >
                          <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "10px" }}>
                            Data: {label}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                            {payload.map((entry, index) => {
                              const series = visibleSeries.find(s => s.label === entry.name);
                              const color = series?.color || entry.color || "hsl(var(--foreground))";
                              return (
                                <div key={index} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <span
                                      style={{
                                        width: "10px",
                                        height: "10px",
                                        borderRadius: "50%",
                                        backgroundColor: color,
                                        display: "inline-block",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <span style={{ color: "hsl(var(--muted-foreground))" }}>
                                      {entry.name}
                                    </span>
                                  </div>
                                  <span style={{ fontWeight: 700, color, fontSize: "14px" }}>
                                    {entry.value}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    }}
                    cursor={{ strokeDasharray: "4 4" }}
                  />
                  {visibleSeries.map((series) => (
                    <Area
                      key={series.key}
                      type="monotone"
                      baseValue={0}
                      dataKey={series.key}
                      stroke={series.color}
                      strokeWidth={periodo === "AllTime" ? 3 : 2.5}
                      fill={`url(#grad-${series.key})`}
                      dot={false}
                      activeDot={{
                        r: 6,
                        fill: series.color,
                        stroke: "#fff",
                        strokeWidth: 2,
                        style: { filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))" }
                      }}
                      name={series.label}
                    />
                  ))}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </section>

      {/* Modal de Seleção de Intervalo Personalizado */}
      <Dialog open={showDateModal} onOpenChange={setShowDateModal}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Selecionar Intervalo de Datas</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Data Inicial</Label>
              <CalendarComponent
                mode="single"
                selected={dateRange.from}
                onSelect={(date) => {
                  setDateRange((prev) => ({ ...prev, from: date }));
                  if (date) setFromMonth(date);
                }}
                month={fromMonth}
                onMonthChange={setFromMonth}
                locale={ptBR}
                className="rounded-md border"
              />
            </div>
            <div className="space-y-2">
              <Label>Data Final</Label>
              <CalendarComponent
                mode="single"
                selected={dateRange.to}
                onSelect={(date) => {
                  setDateRange((prev) => ({ ...prev, to: date }));
                  if (date) setToMonth(date);
                }}
                month={toMonth}
                onMonthChange={setToMonth}
                locale={ptBR}
                className="rounded-md border"
                disabled={(date) => dateRange.from ? date < dateRange.from : false}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDateModal(false);
                setPeriodo("7d");
                setDateRange({});
                setAppliedDateRange({});
                setFromMonth(new Date());
                setToMonth(new Date());
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (dateRange.from && dateRange.to) {
                  setAppliedDateRange({ from: dateRange.from, to: dateRange.to });
                  setShowDateModal(false);
                  setPeriodo("custom");
                }
              }}
              disabled={!dateRange.from || !dateRange.to}
            >
              Aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Ano */}
      <Dialog
        open={showYearModal}
        onOpenChange={(open) => {
          setShowYearModal(open);
          if (!open && !appliedAno) {
            setPeriodo("7d");
            setAno(undefined);
          }
        }}
      >
        <DialogContent className="sm:max-w-[325px]">
          <DialogHeader>
            <DialogTitle>Selecionar Ano</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="ano-input">Ano</Label>
              <Input
                id="ano-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Ex: 2024"
                value={ano ?? ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const parsed = value === "" ? undefined : parseInt(value, 10);
                  setAno(Number.isNaN(parsed) ? undefined : parsed);
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowYearModal(false);
                setPeriodo("7d");
                setAno(undefined);
                setAppliedAno(undefined);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (ano) {
                  setAppliedAno(ano);
                  setShowYearModal(false);
                  setPeriodo("Anual");
                }
              }}
              disabled={!ano}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pontos de Atenção */}
      {pontosAtencao.length > 0 && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="heading-subsection text-foreground">Pontos de Atenção</h3>
              <p className="text-caption mt-0.5">Demandas em aberto há mais de 30 dias</p>
            </div>
          </div>
          <div className="surface-elevated p-6">
            <div className="space-y-3">
              {pontosAtencao.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-border/50 hover:bg-surface-container/50 cursor-pointer transition-colors"
                  onClick={async () => {
                    const encryptedId = await encryptIdForUrl(s.id);
                    navigate(`/solicitacoes/${encryptedId}`);
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-8 w-8 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{s.topico?.descricao || s.status || "Sem título"}</p>
                      <p className="text-xs text-muted-foreground">
                        {s.upn || s.id.slice(0, 8).toUpperCase()} · {s.agenteResponsavel?.nome || "Não atribuído"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs text-muted-foreground">{s.diasAberto} dias em aberto</span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">
                      {formatDate(s.dataCriacao)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Recentes */}
        <ChartCard
          title="Solicitações Recentes"
          subtitle="Últimas atualizações"
        >
          <div className="space-y-3">
            {recentes.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nenhuma solicitação
              </p>
            ) : (
              recentes.map((s) => (
                <div
                  key={s.id}
                  className="cursor-pointer hover:bg-surface-container/50 p-3 rounded-lg border border-border/50 transition-colors"
                  onClick={async () => {
                    // Navegar para detalhes da solicitação com id criptografado
                    const encryptedId = await encryptIdForUrl(s.id);
                    navigate(`/solicitacoes/${encryptedId}`);
                  }}
                >
                  <div className="flex items-start gap-3">
                    {s.anexos?.[0]?.arquivo ? (
                      <img
                        src={s.anexos[0].arquivo}
                        alt="Foto da solicitação"
                        className="h-10 w-10 rounded-md object-cover border border-border flex-shrink-0"
                        loading="lazy"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center border border-border flex-shrink-0">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-[10px] text-primary font-bold whitespace-nowrap">
                          {s.upn ? `#${s.upn}` : s.id.slice(0, 8).toUpperCase()}
                        </span>
                        <SolicitacaoStatusBadge
                          status={s.status as StatusSolicitacao}
                          estadoAprovacao={s.estadoAprovacao}
                        />
                        {s.totalApoiadores > 0 && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0.5 rounded-md">
                            <GradientFavoriteIcon className="h-3 w-3" />
                            {s.totalApoiadores}
                          </span>
                        )}
                      </div>
                      <div className="mb-1">
                        <p className="font-medium text-sm line-clamp-1">
                          {s.topico?.descricao || s.status || "Sem título"}
                        </p>
                        {s.topico?.etiquetas && s.topico.etiquetas.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {s.topico.etiquetas[0]}
                          </p>
                        )}
                      </div>
                      {s.endereco?.bairro && (
                        <div className="flex items-center gap-1 mb-1">
                          <MapPin className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground truncate">
                            {s.endereco.bairro}
                          </span>
                        </div>
                      )}
                      <span className="text-xs text-muted-foreground block">
                        {formatDate(s.dataCriacao)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ChartCard>
    </div>
  );
}
